import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, PortCall, PortCallPurpose } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const PORT_OFFICER_ROLES = ['PORT_OFFICER'];
const PURPOSES: PortCallPurpose[] = ['CARGO', 'BUNKERING', 'CREW_CHANGE', 'TRANSIT', 'TOURISM_CRUISE', 'OTHER'];
const STATES = ['SUBMITTED', 'CLEARANCE_PENDING', 'CLEARED', 'DEPARTED', 'REJECTED', 'CANCELLED'];

/** M22 — the maritime single window: port call submission, health/immigration/customs clearance fan-out, dues, departure. */
export function PortCalls() {
  const { persona } = useAuth();
  const isPortOfficer = persona ? persona.roles.some((r) => PORT_OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m22/port-calls?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<PortCall>>(path);

  return (
    <div>
      <PageHeader
        title="Port Calls"
        subtitle="Maritime single window: health/immigration/customs clearance fan-out, port dues, departure (M22 — SPA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-44" placeholder="Call ref, vessel, port…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isPortOfficer ? (
              <button className="btn-primary" onClick={() => setShowSubmit(true)}>
                + Log port call
              </button>
            ) : null}
          </>
        }
      />

      {!isPortOfficer ? <RoleNotice roles={PORT_OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No port calls match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Call ref</th>
                <th className="table-head">Vessel</th>
                <th className="table-head">Port</th>
                <th className="table-head">ETA</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{p.callRef}</td>
                  <td className="table-cell">{p.vesselNameExternal}</td>
                  <td className="table-cell text-xs text-slate-500">{p.portName}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(p.eta)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showSubmit ? (
        <SubmitPortCallModal
          onClose={() => setShowSubmit(false)}
          onCreated={() => {
            setShowSubmit(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <PortCallDetailModal
          id={selectedId}
          isPortOfficer={isPortOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function SubmitPortCallModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [vesselNameExternal, setVesselNameExternal] = useState('');
  const [grossTonnageExternal, setGrossTonnageExternal] = useState('');
  const [imoNumberExternal, setImoNumberExternal] = useState('');
  const [flagStateExternal, setFlagStateExternal] = useState('');
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [operatorId, setOperatorId] = useState('');
  const [portName, setPortName] = useState('Port Victoria');
  const [purpose, setPurpose] = useState<PortCallPurpose>('CARGO');
  const [eta, setEta] = useState('');
  const [etd, setEtd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m22/port-calls', {
        vesselNameExternal: vesselNameExternal.trim(),
        grossTonnageExternal: Number.parseFloat(grossTonnageExternal),
        ...(imoNumberExternal.trim() ? { imoNumberExternal: imoNumberExternal.trim() } : {}),
        ...(flagStateExternal.trim() ? { flagStateExternal: flagStateExternal.trim() } : {}),
        operatorType,
        operatorId: operatorId.trim(),
        portName: portName.trim(),
        purpose,
        eta: new Date(eta).toISOString(),
        etd: new Date(etd).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log port call');
    } finally {
      setBusy(false);
    }
  };

  const valid = vesselNameExternal.trim() && grossTonnageExternal && operatorId.trim() && portName.trim() && eta && etd;

  return (
    <Modal title="Log a port call" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Vessel name">
          <input className="field-input" value={vesselNameExternal} onChange={(e) => setVesselNameExternal(e.target.value)} />
        </Field>
        <Field label="Gross tonnage">
          <input className="field-input" type="number" value={grossTonnageExternal} onChange={(e) => setGrossTonnageExternal(e.target.value)} />
        </Field>
        <Field label="IMO number (optional)">
          <input className="field-input" value={imoNumberExternal} onChange={(e) => setImoNumberExternal(e.target.value)} />
        </Field>
        <Field label="Flag state (optional)">
          <input className="field-input" value={flagStateExternal} onChange={(e) => setFlagStateExternal(e.target.value)} />
        </Field>
        <Field label="Operator type">
          <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="ENTITY">Entity (shipping agent)</option>
            <option value="PERSON">Person</option>
          </select>
        </Field>
        <Field label="Operator id">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Port">
          <input className="field-input" value={portName} onChange={(e) => setPortName(e.target.value)} />
        </Field>
        <Field label="Purpose">
          <select className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value as PortCallPurpose)}>
            {PURPOSES.map((p) => (
              <option key={p} value={p}>
                {p.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="ETA">
          <input className="field-input" type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} />
        </Field>
        <Field label="ETD">
          <input className="field-input" type="datetime-local" value={etd} onChange={(e) => setEtd(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Submitting…' : 'Submit — fans out clearances + computes dues'}
      </button>
    </Modal>
  );
}

function PortCallDetailModal({ id, isPortOfficer, onClose, onChanged }: { id: string; isPortOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<PortCall>(`/api/v1/m22/port-calls/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const p = q.data;

  return (
    <Modal title={p ? `${p.callRef} — ${p.vesselNameExternal}` : 'Port call'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={p.state} />} />
            <Detail label="Purpose" value={p.purpose.replaceAll('_', ' ')} />
            <Detail label="Port dues" value={scr(p.duesAmount)} />
            <Detail label="ETA" value={formatDateTime(p.eta)} />
            <Detail label="ETD" value={formatDateTime(p.etd)} />
            {p.atd ? <Detail label="ATD (departed)" value={formatDateTime(p.atd)} /> : null}
            <Detail label="Dues paid" value={p.duesPaidAt ? formatDateTime(p.duesPaidAt) : 'Awaiting payment'} />
            {p.berthRequested ? <Detail label="Berth" value={p.berthRequested} /> : null}
          </div>
          {p.closeReason ? <ErrorNote message={p.closeReason} /> : null}

          {p.clearances && p.clearances.length > 0 ? (
            <div>
              <div className="field-label">Single-window clearances</div>
              <div className="space-y-1.5">
                {p.clearances.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                    <span>{c.clearanceType}</span>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={c.status} />
                      {isPortOfficer && (c.status === 'PENDING_REFERRAL' || c.status === 'REFERRED') ? (
                        <>
                          <button
                            className="btn-success !py-1 !text-xs"
                            disabled={busy}
                            onClick={() => call(() => api.post(`/api/v1/m22/port-calls/${p.id}/clearances/${c.id}/decide`, { status: 'CLEARED' }))}
                          >
                            Clear
                          </button>
                          <button
                            className="btn-danger !py-1 !text-xs"
                            disabled={busy}
                            onClick={() => {
                              const note = window.prompt('Denial note?') ?? undefined;
                              call(() => api.post(`/api/v1/m22/port-calls/${p.id}/clearances/${c.id}/decide`, { status: 'DENIED', note }));
                            }}
                          >
                            Deny
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {isPortOfficer ? (
            <div className="flex flex-wrap gap-2">
              {p.state === 'CLEARED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/port-calls/${p.id}/depart`))}>
                  {busy ? 'Recording…' : 'Record departure'}
                </button>
              ) : null}
              {p.state === 'SUBMITTED' || p.state === 'CLEARANCE_PENDING' ? (
                !showCancel ? (
                  <button className="btn-danger" onClick={() => setShowCancel(true)}>
                    Cancel port call
                  </button>
                ) : (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/port-calls/${p.id}/cancel`, { reason: reason.trim() }))}
                    >
                      Confirm
                    </button>
                  </div>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={PORT_OFFICER_ROLES} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Submitted {formatDateTime(p.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
